import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Shield, ShieldCheck, Lock, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESOURCE_LABELS = {
  dashboard: 'Dashboard',
  roles: 'Roles & Permissions',
  zones: 'Zones',
  categories: 'Categories',
  admin_categories: 'Admin Categories',
  banners: 'Banners',
  offers: 'Offers',
  explore_more: 'Explore More',
  restaurants: 'Restaurants',
  items: 'Menu Items',
  orders: 'Orders',
  transactions: 'Transactions',
  delivery_agents: 'Delivery Agents',
  agent_locations: 'Agent Locations',
  affiliate: 'Affiliate System',
  referrals: 'Refer & Earn',
  commissions: 'Commission Management',
  reviews: 'Reviews & Ratings',
  users: 'Users',
  notifications: 'Notifications',
  app_icons: 'App Icon',
  launch_screen: 'Launch Screen',
  settings: 'Settings'
  // `admins` is intentionally not listed — only Super Admin can manage admins.
};

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All stats (full dashboard)' },
  { value: 'today', label: "Today's stats only" }
];

const emptyPerms = (resources) =>
  resources.reduce((acc, r) => {
    acc[r] = { view: false, create: false, edit: false, delete: false };
    return acc;
  }, {});

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ resources: [], actions: ['view', 'create', 'edit', 'delete'], labels: {} });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');

  // Dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', permissions: {}, dashboardScope: 'all', status: true });

  const fetchAll = async () => {
    try {
      const [r, m] = await Promise.all([
        axios.get(`${API}/roles`),
        axios.get(`${API}/roles/meta`)
      ]);
      setRoles(r.data || []);
      setMeta(m.data || meta);
    } catch (err) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) =>
      [r.name, r.description].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [roles, search]);

  const openCreate = () => {
    setEditMode(false);
    setCurrent(null);
    setFormData({ name: '', description: '', permissions: emptyPerms(meta.resources), dashboardScope: 'all', status: true });
    setShowDialog(true);
  };

  const openEdit = (role) => {
    setEditMode(true);
    setCurrent(role);
    const perms = emptyPerms(meta.resources);
    for (const r of meta.resources) {
      perms[r] = { ...perms[r], ...(role.permissions?.[r] || {}) };
    }
    setFormData({
      name: role.name || '',
      description: role.description || '',
      permissions: perms,
      dashboardScope: role.dashboardScope || 'all',
      status: role.status !== false
    });
    setShowDialog(true);
  };

  // create/edit/delete need view; removing view removes everything for that page.
  const applyToggle = (cur, action, value) => {
    const next = { ...cur, [action]: value };
    if (value && action !== 'view') next.view = true;
    if (!value && action === 'view') {
      next.create = false; next.edit = false; next.delete = false;
    }
    return next;
  };

  const togglePerm = (resource, action) => {
    if (isLocked) return;
    setFormData((fd) => ({
      ...fd,
      permissions: {
        ...fd.permissions,
        [resource]: applyToggle(fd.permissions[resource] || {}, action, !fd.permissions[resource]?.[action])
      }
    }));
  };

  const toggleResourceAll = (resource) => {
    if (isLocked) return;
    const cur = formData.permissions[resource] || {};
    const allOn = meta.actions.every((a) => cur[a]);
    const next = meta.actions.reduce((acc, a) => ({ ...acc, [a]: !allOn }), {});
    setFormData((fd) => ({
      ...fd,
      permissions: { ...fd.permissions, [resource]: next }
    }));
  };

  const toggleActionAll = (action) => {
    if (isLocked) return;
    const allOn = meta.resources.every((r) => formData.permissions[r]?.[action]);
    setFormData((fd) => {
      const next = { ...fd.permissions };
      for (const r of meta.resources) {
        next[r] = applyToggle(next[r] || {}, action, !allOn);
      }
      return { ...fd, permissions: next };
    });
  };

  // Super Admin always has full access — its matrix is read-only.
  const isLocked = editMode && !!current?.isSystem && String(current?.name).toLowerCase() === 'super admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && current) {
        await axios.patch(`${API}/roles/${current.id}`, formData);
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, formData);
        toast.success('Role created');
      }
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await axios.delete(`${API}/roles/${role.id}`);
      toast.success('Role deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const countActions = (perms) => {
    let total = 0;
    for (const r of meta.resources) {
      const p = perms?.[r] || {};
      for (const a of meta.actions) if (p[a]) total++;
    }
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  const totalSlots = meta.resources.length * meta.actions.length;

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            <Shield size={26} className="text-orange-600" /> Roles & Permissions
          </h1>
          <p className="text-gray-600 text-sm mt-1">Define roles and what each can view, create, edit or delete</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-role-button" onClick={openCreate} style={{ backgroundColor: '#C2410C' }} className="text-white">
              <Plus size={18} className="mr-2" />New Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
            <DialogHeader>
              <DialogTitle>
                {editMode ? `Edit Role: ${current?.name}` : 'Create New Role'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Role Name *</Label>
                  <Input
                    data-testid="role-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={editMode && current?.isSystem}
                    required
                  />
                  {editMode && current?.isSystem && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Lock size={11} />System role — name is locked
                    </p>
                  )}
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>

              {/* DASHBOARD SCOPE + STATUS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Dashboard shows</Label>
                  <select
                    data-testid="role-dashboard-scope"
                    value={formData.dashboardScope}
                    disabled={isLocked}
                    onChange={(e) => setFormData({ ...formData, dashboardScope: e.target.value })}
                    className="w-full h-9 mt-1 text-sm rounded-md border border-gray-300 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-60"
                  >
                    {SCOPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    “Today&apos;s stats only” hides totals, revenue history and finance cards (also on the Orders page).
                  </p>
                </div>
                <div>
                  <Label>Status</Label>
                  <label className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                    <Checkbox
                      data-testid="role-status-checkbox"
                      checked={!!formData.status}
                      disabled={isLocked}
                      onCheckedChange={(v) => setFormData({ ...formData, status: !!v })}
                    />
                    Active (admins with a disabled role cannot access anything)
                  </label>
                </div>
              </div>

              {isLocked && (
                <p className="text-xs text-purple-700 bg-purple-50 rounded px-3 py-2 flex items-center gap-1">
                  <Lock size={12} /> Super Admin always has full access — permissions can&apos;t be changed.
                </p>
              )}

              {/* PERMISSION MATRIX */}
              <div>
                <Label>Permissions</Label>
                <div className="border rounded-lg overflow-hidden mt-2">
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]" data-testid="permissions-matrix">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[35%]">Resource</th>
                        {meta.actions.map((a) => (
                          <th key={a} className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                            <button
                              type="button"
                              data-testid={`toggle-action-${a}`}
                              onClick={() => toggleActionAll(a)}
                              className="hover:text-orange-600 capitalize"
                            >
                              {a}
                            </button>
                          </th>
                        ))}
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">All</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {meta.resources.map((r) => {
                        const perms = formData.permissions[r] || {};
                        const allOn = meta.actions.every((a) => perms[a]);
                        return (
                          <tr key={r} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-800">{(meta.labels?.[r] || RESOURCE_LABELS[r] || r)}</td>
                            {meta.actions.map((a) => (
                              <td key={a} className="px-3 py-2">
                                <Checkbox
                                  data-testid={`perm-${r}-${a}`}
                                  checked={!!perms[a]}
                                  disabled={isLocked}
                                  onCheckedChange={() => togglePerm(r, a)}
                                />
                              </td>
                            ))}
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => toggleResourceAll(r)}
                                className={`text-xs font-medium ${allOn ? 'text-orange-600' : 'text-gray-500'} hover:underline`}
                              >
                                {allOn ? 'Clear' : 'Select'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {countActions(formData.permissions)} / {totalSlots} permissions enabled · click an action header to toggle that column
                </p>
              </div>

              <Button type="submit" className="w-full" style={{ backgroundColor: '#C2410C' }}>
                {editMode ? 'Save Changes' : 'Create Role'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="roles-search-input"
            placeholder="Search roles..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="ml-auto text-sm text-gray-600">
          {filtered.length} of {roles.length} roles
        </div>
      </div>

      {/* GRID OF ROLE CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => {
          const enabled = countActions(r.permissions);
          return (
            <div
              key={r.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
              data-testid={`role-card-${r.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                    <ShieldCheck size={20} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{r.name}</h3>
                    <p className="text-xs text-gray-500">{r.description || '—'}</p>
                  </div>
                </div>
                {r.isSystem && (
                  <Badge className="bg-purple-100 text-purple-800 border-0 text-[10px] flex items-center gap-1">
                    <Lock size={10} />System
                  </Badge>
                )}
              </div>

              {(r.dashboardScope === 'today' || r.status === false) && (
                <div className="flex gap-1 flex-wrap mb-2">
                  {r.dashboardScope === 'today' && (
                    <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px]">Today&apos;s stats only</Badge>
                  )}
                  {r.status === false && (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Disabled</Badge>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs">
                <div className="bg-orange-50 rounded p-2">
                  <p className="text-gray-600">Permissions</p>
                  <p className="font-semibold text-orange-700">{enabled} / {totalSlots}</p>
                </div>
                <div className="bg-blue-50 rounded p-2">
                  <p className="text-gray-600">Admins assigned</p>
                  <p className="font-semibold text-blue-700">{r.adminCount || 0}</p>
                </div>
              </div>

              {/* Mini permission summary */}
              <div className="mb-4 max-h-28 overflow-y-auto pr-1">
                {meta.resources.map((res) => {
                  const p = r.permissions?.[res] || {};
                  const enabledActs = meta.actions.filter((a) => p[a]);
                  if (enabledActs.length === 0) return null;
                  return (
                    <div key={res} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="text-gray-700 w-32 truncate">{(meta.labels?.[res] || RESOURCE_LABELS[res] || res)}:</span>
                      <div className="flex gap-1 flex-wrap">
                        {enabledActs.map((a) => (
                          <Badge key={a} className="bg-emerald-100 text-emerald-700 border-0 text-[10px] capitalize">
                            {a}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid={`edit-role-${r.id}`} onClick={() => openEdit(r)} className="flex-1">
                  <Edit size={14} className="mr-1" /> Edit
                </Button>
                {!r.isSystem && (
                  <Button size="sm" variant="destructive" data-testid={`delete-role-${r.id}`} onClick={() => remove(r)}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-gray-500 py-8">No roles found</p>
      )}
    </div>
  );
};

export default Roles;
